// Doctor Jobs Profile Context - Adapted from your other product
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "../supabase";
import { useAuth } from "./AuthContext";
import useSnackbarNotification from "../hooks/useSnackbarNotification";
import { subscribeToResendAudience } from "../hooks/useResendAudience";

const ProfileInfoContext = createContext(undefined);

export const useProfileInfo = () => {
  const context = useContext(ProfileInfoContext);
  if (!context) {
    throw new Error("useProfileInfo must be used within a ProfileInfoProvider");
  }
  return context;
};

export const ProfileInfoProvider = ({ children }) => {
  const { user } = useAuth();
  /** Stable across TOKEN_REFRESHED / tab focus — avoids remounting layouts when only the session object changes */
  const userId = user?.id;
  const { showError } = useSnackbarNotification();
  const [profileInfo, setProfileInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const normalizeProfile = (profile) => {
    if (!profile || typeof profile !== 'object') return profile;
    if (profile.companyname ?? profile.company_name) {
      return {
        ...profile,
        companyname: profile.companyname ?? profile.company_name ?? null,
      };
    }
    return {
      ...profile,
      companyname: null,
    };
  };

  const getProfileInfo = useCallback(async (forceRefresh = false) => {
    if (!userId) {
      console.warn("User not available");
      return null;
    }

    const cacheKey = `profile_${userId}`;
    
    // If force refresh, clear cache first
    if (forceRefresh) {
      try {
        sessionStorage.removeItem(cacheKey);
      } catch (e) {
        // Failed to clear cache
      }
    }

    // Check cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          const cachedData = JSON.parse(cached);
          const normalized = normalizeProfile(cachedData);
          setProfileInfo(normalized);
          return normalized;
        } catch (e) {
          sessionStorage.removeItem(cacheKey);
        }
      }
    }

    // Fetch from database
    const { data, error } = await supabase
      .from("users_profiles")
      .select("*")
      .eq("profileid", userId)
      .single();

    if (error || !data) {
      return null;
    }

    const normalized = normalizeProfile(data);

    // Cache for 5 minutes
    sessionStorage.setItem(cacheKey, JSON.stringify(normalized));
    setTimeout(() => sessionStorage.removeItem(cacheKey), 5 * 60 * 1000);

    setProfileInfo(normalized);
    return normalized;
  }, [userId]);

  useEffect(() => {
    if (userId) {
      setLoading(true);
      // Fetch when the logged-in user identity changes (login / switch account), not on token refresh
      getProfileInfo(true).finally(() => setLoading(false));
    } else {
      setProfileInfo(null);
      setLoading(false);
      try {
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('profile_')) {
            sessionStorage.removeItem(key);
          }
        });
      } catch (e) {
        // Ignore storage errors
      }
    }
  }, [userId, getProfileInfo]);

  // Listen for storage events and custom events to detect when cache is cleared (e.g., by admin verification)
  useEffect(() => {
    if (!userId) return;

    const cacheKey = `profile_${userId}`;
    
    const handleStorageChange = (e) => {
      // If our profile cache was removed, refresh the profile
      if (e.key === cacheKey && e.newValue === null) {
        getProfileInfo(true);
      }
    };

    const handleCustomEvent = (e) => {
      // If the cache was cleared for this user, refresh
      if (e.detail?.userId === userId) {
        getProfileInfo(true);
      }
    };

    // Listen for storage events (triggered when cache is cleared in another tab/window)
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for custom events (triggered when cache is cleared in same tab)
    window.addEventListener('profileCacheCleared', handleCustomEvent);

    // Also check periodically if cache was cleared (fallback for same-tab cache clearing)
    const checkInterval = setInterval(() => {
      const cached = sessionStorage.getItem(cacheKey);
      // If we have profileInfo but cache was cleared, refresh
      if (profileInfo && !cached) {
        getProfileInfo(true);
      }
    }, 3000); // Check every 3 seconds (less aggressive)

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('profileCacheCleared', handleCustomEvent);
      clearInterval(checkInterval);
    };
  }, [userId, profileInfo, getProfileInfo]);

  // Subscribe to realtime updates so verification changes propagate instantly
  useEffect(() => {
    if (!userId) return;

    const cacheKey = `profile_${userId}`;

    const channel = supabase
      .channel(`profile_updates_${userId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'users_profiles', filter: `profileid=eq.${userId}` },
        (payload) => {
          if (!payload?.new) return;
          const updatedProfile = normalizeProfile(payload.new);
          
          // CRITICAL: BLOCK role changes via realtime updates
          // Role should NEVER change after initial creation - if it does, reject the update
          if (profileInfo && profileInfo.role && updatedProfile.role && profileInfo.role !== updatedProfile.role) {
            console.error(`[ProfileInfoContext] BLOCKED: Role change attempt detected from ${profileInfo.role} to ${updatedProfile.role} via database update. Rejecting unauthorized role change.`);
            
            // Fetch current role from database to verify what it actually is
            (async () => {
              const { data: dbProfile, error } = await supabase
                .from("users_profiles")
                .select("role")
                .eq("profileid", userId)
                .single();
              
              if (!error && dbProfile?.role) {
                // If database role matches our current role, keep it
                // If database role is different, something is wrong - refresh from database
                if (dbProfile.role === profileInfo.role) {
                  console.log(`[ProfileInfoContext] Database role matches current role (${profileInfo.role}), keeping current state`);
                } else {
                  console.error(`[ProfileInfoContext] Database role (${dbProfile.role}) differs from current (${profileInfo.role}). Refreshing profile.`);
                  getProfileInfo(true); // Force refresh from database
                }
              }
            })();
            
            // REJECT the update - don't update local state with unauthorized role change
            return;
          }
          
          // Persist latest profile in cache
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(updatedProfile));
          } catch (e) {
            console.warn('Failed to cache realtime profile update:', e);
          }

          setProfileInfo(updatedProfile);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, profileInfo, getProfileInfo]);

  const createProfileInfo = async (data) => {
    if (!user) throw new Error("User not authenticated");

    // CRITICAL: Check if profile already exists and preserve existing role
    // This prevents overwriting an Employer role with Employee (or vice versa)
    let existingProfile = null;
    let existingRole = null;
    let profileCheckFailed = false;
    
    try {
      const { data: profileData, error: profileError } = await supabase
        .from("users_profiles")
        .select("role")
        .eq("profileid", user.id)
        .single();
      
      // Check for Supabase error response
      if (profileError) {
        // Only treat PGRST116 (not found) as "profile doesn't exist"
        // All other errors should fail safely to prevent role switching
        if (profileError.code === 'PGRST116') {
          // Profile doesn't exist - this is fine, we can create it
          console.log('[createProfileInfo] No existing profile found (PGRST116), creating new one');
        } else {
          // Any other error (network, timeout, permission, etc.) - fail safely
          console.error(`[createProfileInfo] Error checking existing profile:`, profileError);
          profileCheckFailed = true;
          throw new Error(`Failed to verify existing profile. Cannot proceed with role creation. Please try again or contact support. Error: ${profileError.message || 'Unknown error'}`);
        }
      } else if (profileData) {
        // Profile exists - preserve the role
        existingProfile = profileData;
        existingRole = profileData.role;
        console.log(`[createProfileInfo] Existing profile found with role: ${existingRole}`);
      }
    } catch (e) {
      // If we already set profileCheckFailed, re-throw the error
      if (profileCheckFailed) {
        throw e;
      }
      // For unexpected exceptions, fail safely
      console.error(`[createProfileInfo] Unexpected error checking profile:`, e);
      throw new Error(`Failed to verify existing profile. Cannot proceed with role creation. Please try again or contact support.`);
    }

    // CRITICAL: If profile exists with a role, NEVER overwrite it
    // This is the key protection against role switching
    if (existingRole && existingRole !== data.role) {
      const errorMsg = `Cannot change role from ${existingRole} to ${data.role}. Role changes are not allowed after initial account creation.`;
      console.error(`[createProfileInfo] BLOCKED: ${errorMsg}`);
      throw new Error(errorMsg);
    }

    // Use existing role if available, otherwise use the provided role
    const roleToUse = existingRole || data.role;
    
    // Log if preserving existing role
    if (existingRole) {
      console.log(`[createProfileInfo] Preserving existing role: ${existingRole}`);
    }

    const isEmployer = (roleToUse ?? user?.user_metadata?.role) === 'Employer';

    const insertData = {
      // map camelCase -> lowercase column names expected by Postgres
      profileid: user.id,
      email: user.email,
      role: roleToUse, // Use preserved role or new role
      onboardingstep: data.onboardingStep ?? data.onboardingstep,
      onboardingcompleted: data.onboardingCompleted ?? data.onboardingcompleted,
      firsttimelogin: data.firstTimeLogin ?? data.firsttimelogin,
      
      // Basic user info (for candidate display)
      firstname: user.user_metadata?.first_name || data.firstName,
      lastname: user.user_metadata?.last_name || data.lastName,
      
      // Employee personal info
      cellnumber: user.user_metadata?.cell_number || data.cellNumber,
      dateofbirth: user.user_metadata?.date_of_birth || data.dateOfBirth,
      sex: user.user_metadata?.sex || data.sex,
      linkedinurl: user.user_metadata?.linkedin_url || data.linkedinUrl,
      jobsearchstatus: user.user_metadata?.job_search_status || data.jobSearchStatus,
      hpcsa_reg_no: user.user_metadata?.hpcsa_reg_no || data.hpcsaRegNo,
      location_city_country: user.user_metadata?.location_city_country || user.user_metadata?.country || data.locationCityCountry,
      profile_image_url: user.user_metadata?.profile_image_url || data.profileImageUrl,
      profile_about: user.user_metadata?.profile_about || data.aboutText,
      profile_visibility: user.user_metadata?.profile_visibility || data.profileVisibility,
      car_license: user.user_metadata?.profile_car_license || data.carLicense,
      
      // Employee professional info
      profession: user.user_metadata?.profession || data.profession,
      years_experience: user.user_metadata?.years_experience || data.yearsExperience,
      cv_url: user.user_metadata?.profile_cv_url || data.cvUrl,
      cv_filename: user.user_metadata?.profile_cv_filename || data.cvFilename,
      
      // Skills and experience (as JSON)
      skills: user.user_metadata?.profile_skills ? JSON.stringify(user.user_metadata.profile_skills) : null,
      languages: user.user_metadata?.profile_languages ? JSON.stringify(user.user_metadata.profile_languages) : null,
      work_experiences: user.user_metadata?.profile_experiences ? JSON.stringify(user.user_metadata.profile_experiences) : null,
      educations: user.user_metadata?.profile_educations ? JSON.stringify(user.user_metadata.profile_educations) : null,
      certifications: user.user_metadata?.profile_certifications ? JSON.stringify(user.user_metadata.profile_certifications) : null,
      
      // Role preferences (as JSON)
      selected_categories: user.user_metadata?.my_profile?.profile_role?.categories ? JSON.stringify(user.user_metadata.my_profile.profile_role.categories) : null,
      selected_roles: user.user_metadata?.my_profile?.profile_role?.roles ? JSON.stringify(user.user_metadata.my_profile.profile_role.roles) : null,
      employment_types: user.user_metadata?.my_profile?.profile_role?.employmentTypes ? JSON.stringify(user.user_metadata.my_profile.profile_role.employmentTypes) : null,
    };

    if (isEmployer) {
      // Get company info from metadata as fallback (for onboarding data)
      const companyInfo = user?.user_metadata?.employer_profile?.companyInfo;
      
      // Only save basic employer profile fields to users_profiles
      // Job-specific fields (industry, jobtitle, specialty, etc.) belong in internal_job_listings, not users_profiles
      // Only saving companyname since it's the only field confirmed to exist in users_profiles (it's normalized and read)
      // workemail and logofilename may not exist in users_profiles schema - keeping them out to prevent schema errors
      const employerFields = {
        companyname: data.companyName ?? data.companyname ?? companyInfo?.companyName,
        // Removed workemail and logofilename - these may not exist in users_profiles table
        // Removed job-specific fields - these belong in internal_job_listings:
        // industry, jobtitle, specialty, joblocation, employmenttype, workpreference,
        // qualifications, requireslicense, sponsorship, companyurl, company_description
      };
      
      // Filter out undefined/null/empty string values to avoid schema errors for missing columns
      const filteredEmployerFields = Object.fromEntries(
        Object.entries(employerFields).filter(([_, value]) => value !== undefined && value !== null && value !== "")
      );
      
      Object.assign(insertData, filteredEmployerFields);
    }

    // Filter out empty strings from all fields to prevent integer type errors
    const filteredInsertData = Object.fromEntries(
      Object.entries(insertData).filter(([_, value]) => value !== undefined && value !== null && value !== "")
    );

    // CRITICAL: Final safety check before upsert - double-check existing role one more time
    // This prevents race conditions where role might have been set between our check and upsert
    if (!existingRole) {
      const { data: finalCheck, error: finalCheckError } = await supabase
        .from("users_profiles")
        .select("role")
        .eq("profileid", user.id)
        .single();
      
      if (finalCheckError && finalCheckError.code !== 'PGRST116') {
        // Error checking - fail safely
        console.error(`[createProfileInfo] Final check error:`, finalCheckError);
        throw new Error(`Failed to verify profile before creation. Please try again.`);
      }
      
      if (finalCheck?.role && finalCheck.role !== roleToUse) {
        // Role exists and doesn't match - block the operation
        const errorMsg = `Cannot change role from ${finalCheck.role} to ${roleToUse}. Role changes are not allowed after initial account creation.`;
        console.error(`[createProfileInfo] BLOCKED at final check: ${errorMsg}`);
        throw new Error(errorMsg);
      }
    }

    // Upsert to avoid duplicate key errors if profile already exists
    const { error } = await supabase
      .from("users_profiles")
      .upsert([filteredInsertData], { onConflict: "profileid" });

    if (error) throw new Error(error.message);
    await getProfileInfo();
      try {
        await subscribeToResendAudience({
          email: user.email,
          firstName: user.user_metadata.first_name,
          lastName: user.user_metadata.last_name,
          role: data.role,
        });

        await supabase.auth.updateUser({
          data: { isFirstTimeUser: false },
        });
      } catch (err) {
        console.error("Failed to subscribe user to Resend audience:", err);
      }
  };

  const updateProfileInfo = async (data) => {
    if (!user) throw new Error("User not authenticated");

    // CRITICAL: Prevent role changes after initial profile creation
    // Role can only be set during createProfileInfo, never changed via updateProfileInfo
    // Always check database for role, never rely on cached state
    let dbRole = null;
    if (data.role !== undefined && data.role !== null) {
      // CRITICAL: Always fetch from database to get the actual role, not cached state
      const { data: dbProfile, error: dbError } = await supabase
        .from("users_profiles")
        .select("role")
        .eq("profileid", user.id)
        .single();
      
      if (dbError && dbError.code !== 'PGRST116') {
        // Error fetching profile - fail safely
        console.error(`[updateProfileInfo] Error checking existing role:`, dbError);
        throw new Error("Failed to verify existing profile. Cannot update profile. Please try again.");
      }
      
      dbRole = dbProfile?.role;
      if (dbRole && dbRole !== data.role) {
        console.error(`[updateProfileInfo] BLOCKED: Attempted to change role from ${dbRole} to ${data.role}. Role changes are not allowed after initial creation.`);
        throw new Error("Role cannot be changed after initial account creation. Please contact support if you need to change your account type.");
      }
      // If no current role exists, allow setting it (for edge cases during initial creation)
      // If role matches current role, allow it (no-op)
    }

    // Determine isEmployer from database role first, then fallback to cached state, then metadata
    // Fetch role if we haven't already
    if (dbRole === null) {
      const { data: dbProfileForRole, error: roleError } = await supabase
        .from("users_profiles")
        .select("role")
        .eq("profileid", user.id)
        .single();
      // Ignore errors, use fallback
      if (!roleError && dbProfileForRole) {
        dbRole = dbProfileForRole?.role;
      }
    }
    
    const actualRole = dbRole ?? profileInfo?.role ?? user?.user_metadata?.role;
    const isEmployer = actualRole === 'Employer';

    const updateData = {
      // NEVER include role in update - role can only be set via createProfileInfo
      // This completely prevents any possibility of role changes through updateProfileInfo
      onboardingstep: data.onboardingStep ?? data.onboardingstep,
      onboardingcompleted: data.onboardingCompleted ?? data.onboardingcompleted,
      firsttimelogin: data.firstTimeLogin ?? data.firsttimelogin,
      profile_visibility: data.profileVisibility ?? data.profile_visibility,
      updatedat: new Date().toISOString(),
    };

    if (isEmployer) {
      // Get company info from metadata as fallback (for onboarding data)
      const companyInfo = user?.user_metadata?.employer_profile?.companyInfo;
      
      // Only save basic employer profile fields to users_profiles
      // Job-specific fields (industry, jobtitle, specialty, etc.) belong in internal_job_listings, not users_profiles
      // Only saving companyname since it's the only field confirmed to exist in users_profiles (it's normalized and read)
      // workemail and logofilename may not exist in users_profiles schema - keeping them out to prevent schema errors
      const employerFields = {
        companyname: data.companyName ?? data.companyname ?? companyInfo?.companyName,
        // Removed workemail and logofilename - these may not exist in users_profiles table
        // Removed job-specific fields - these belong in internal_job_listings:
        // industry, jobtitle, specialty, joblocation, employmenttype, workpreference,
        // qualifications, requireslicense, sponsorship, companyurl, company_description
      };
      
      // Filter out undefined/null/empty string values to avoid schema errors for missing columns
      const filteredEmployerFields = Object.fromEntries(
        Object.entries(employerFields).filter(([_, value]) => value !== undefined && value !== null && value !== "")
      );
      
      Object.assign(updateData, filteredEmployerFields);
    }

    // Filter out empty strings from all fields to prevent integer type errors
    const filteredUpdateData = Object.fromEntries(
      Object.entries(updateData).filter(([_, value]) => value !== undefined && value !== null && value !== "")
    );

    const { data: updated, error } = await supabase
      .from("users_profiles")
      .update(filteredUpdateData)
      .eq("profileid", user.id)
      .select("*")
      .single();

    if (error) throw new Error(error.message);
    
    if (updated) {
      // Update cache
      const cacheKey = `profile_${user.id}`;
      const normalized = normalizeProfile(updated);
      sessionStorage.setItem(cacheKey, JSON.stringify(normalized));
      setProfileInfo(normalized);
    }
  };

  const migrateFromMetadata = async () => {
    if (!user) return;

    try {
      const metadata = user.user_metadata;
      if (!metadata) return;

      // Check if profile already exists - fetch full profile to check role
      const { data: existingProfile, error: profileCheckError } = await supabase
        .from("users_profiles")
        .select("profileid, role")
        .eq("profileid", user.id)
        .single();

      // If profile exists, don't migrate (preserve existing role)
      if (existingProfile) {
        console.log(`[migrateFromMetadata] Profile already exists with role: ${existingProfile.role}`);
        return;
      }

      // If error is NOT "not found", don't proceed (could be a real error)
      if (profileCheckError && profileCheckError.code !== 'PGRST116') {
        console.error('[migrateFromMetadata] Error checking profile:', profileCheckError);
        return;
      }

      // Determine role from metadata - check if user has employer_profile
      const isEmployer = metadata.employer_profile && Object.keys(metadata.employer_profile).length > 0;
      const role = isEmployer ? 'Employer' : 'Employee';

      // Migrate data from metadata
      const profileData = {
        role: role, // Set role based on metadata (Employer or Employee)
        fullName: metadata.first_name && metadata.last_name 
          ? `${metadata.first_name} ${metadata.last_name}` 
          : null,
        employeeLocation: metadata.location_city_country || metadata.country,
        linkedinUrl: metadata.linkedin_url,
        jobSearchStatus: metadata.job_search_status,
        hpcsaRegNo: metadata.hpcsa_reg_no,
        dateOfBirth: metadata.date_of_birth,
        sex: metadata.sex,
        cvUrl: metadata.profile_cv_url,
        cvPath: metadata.profile_cv_path,
        cvFilename: metadata.profile_cv_filename,
        aboutText: metadata.profile_about,
        profileVisibility: metadata.profile_visibility || 'hidden',
        carLicense: metadata.profile_car_license,
        skills: metadata.profile_skills?.skills || [],
        languages: metadata.profile_skills?.languages || [],
        workExperiences: metadata.profile_experience?.experiences || [],
        educations: metadata.profile_experience?.educations || [],
        certifications: metadata.profile_experience?.certifications || [],
        onboardingCompleted: true, // Mark as completed since they have data
        firstTimeLogin: false
      };

      // Add employer-specific fields if user is an employer
      if (isEmployer && metadata.employer_profile?.companyInfo) {
        const companyInfo = metadata.employer_profile.companyInfo;
        profileData.companyName = companyInfo.companyName;
        profileData.industry = companyInfo.industry;
        profileData.companyUrl = companyInfo.companyUrl;
        profileData.workEmail = companyInfo.workEmail;
        profileData.logoFileName = companyInfo.logoFileName;
        profileData.companyAbout = companyInfo.about;
      }

      await createProfileInfo(profileData);
      
    } catch (error) {
      console.error('[migrateFromMetadata] Error migrating profile:', error);
      showError("Failed to migrate profile data");
    }
  };

  return (
    <ProfileInfoContext.Provider
      value={{ 
        profileInfo, 
        loading, 
        createProfileInfo, 
        getProfileInfo, 
        updateProfileInfo,
        migrateFromMetadata
      }}
    >
      {children}
    </ProfileInfoContext.Provider>
  );
};
